import { Box, Grid, Text } from "@mantine/core";
import { useState } from "react";

import {
  AxisSwitchProps,
  FormInputCategory,
  QueryResultColumn,
} from "../../../utils/Types";
import Form from "../../Form";
import { getDefaultAxis } from "./getDefaultAxis";

export type AxisSwitchFieldProps = {
  axisName: "X" | "Y";
  optionsOfAxis?: QueryResultColumn[];
  defaultAxisName?: string;
  exposeAxis?: (v: QueryResultColumn | undefined) => void;
  handleChartStateChangeAxis?: (v: QueryResultColumn | undefined) => void;
};
export const AxisSwitchField = ({
  axisName,
  optionsOfAxis,
  defaultAxisName,
  exposeAxis,
  handleChartStateChangeAxis,
}: AxisSwitchFieldProps) => {
  const [axis, setAxis] = useState<QueryResultColumn | undefined>(
    getDefaultAxis({
      options: optionsOfAxis,
      defaultName: defaultAxisName,
      isY: false,
    }),
  );

  return (
    // Cap the single-axis select's width so it doesn't stretch to fill the
    // space-between chart header (matches the X/Y `AxisSwitch` columns'
    // `maw: 200`). Without this the `Form fullWidth` grows to full width.
    <Box w="min(200px, 100%)">
      <Form
        fullWidth
        isHorizontal
        inputs={[
          {
            name: axisName + "Axis",
            category: FormInputCategory.SELECT,
            options:
              optionsOfAxis?.map((i) => ({ label: i.label, value: i.name })) ||
              [],
            defaultValue: axis?.name,
            size: "small",
            inputProps: {
              startAdornment: <Text fw={700}>{axisName}</Text>,
            },
            exposeValue: (v) => {
              const axis = optionsOfAxis?.find((i) => i.name === v);
              setAxis(axis);
              exposeAxis?.(axis);
              handleChartStateChangeAxis?.(axis);
            },
          },
        ]}
      />
    </Box>
  );
};

const AxisSwitch = ({
  optionsOfXAxis,
  optionsOfYAxis,
  defaultXAxisName,
  defaultYAxisName,
  exposeXAxis,
  exposeYAxis,
  handleChartStateChangeXAxis,
  handleChartStateChangeYAxis,
}: AxisSwitchProps) => {
  const [xAxis, setXAxis] = useState<QueryResultColumn | undefined>(
    getDefaultAxis({
      options: optionsOfXAxis,
      defaultName: defaultXAxisName,
      isY: false,
    }),
  );
  const [yAxis, setYAxis] = useState<QueryResultColumn | undefined>(
    getDefaultAxis({
      options: optionsOfYAxis,
      defaultName: defaultYAxisName,
      isY: true,
    }),
  );

  return (
    <Grid w="100%" gap={8}>
      {optionsOfXAxis && (
        <Grid.Col span={{ base: 12, md: 6 }} maw={{ md: 200 }} miw={0}>
          <Form
            fullWidth
            isHorizontal
            inputs={[
              {
                name: "xAxis",
                category: FormInputCategory.SELECT,
                options:
                  optionsOfXAxis
                    ?.filter((i) => i.name !== yAxis?.name)
                    .map((i) => ({ label: i.label, value: i.name })) || [],
                defaultValue: xAxis?.name,
                size: "small",
                inputProps: {
                  startAdornment: <Text fw={700}>X</Text>,
                },
                exposeValue: (v) => {
                  const axis = optionsOfXAxis?.find((i) => i.name === v);
                  setXAxis(axis);
                  exposeXAxis?.(axis);
                  handleChartStateChangeXAxis?.(axis);
                },
              },
            ]}
          />
        </Grid.Col>
      )}
      {optionsOfYAxis && (
        <Grid.Col span={{ base: 12, md: 6 }} maw={{ md: 200 }} miw={0}>
          <Form
            fullWidth
            isHorizontal
            inputs={[
              {
                name: "yAxis",
                category: FormInputCategory.SELECT,
                options:
                  optionsOfYAxis
                    ?.filter((i) => i.name !== xAxis?.name)
                    .map((i) => ({ label: i.label, value: i.name })) || [],
                defaultValue: yAxis?.name,
                size: "small",
                inputProps: {
                  startAdornment: <Text fw={700}>Y</Text>,
                },
                exposeValue: (v) => {
                  const axis = optionsOfYAxis?.find((i) => i.name === v);
                  setYAxis(axis);
                  exposeYAxis?.(axis);
                  handleChartStateChangeYAxis?.(axis);
                },
              },
            ]}
          />
        </Grid.Col>
      )}
    </Grid>
  );
};

export default AxisSwitch;
